import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Loader2, CreditCard, Calendar, AlertCircle, Download, ExternalLink, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { openCustomerPortal } from '@/services/stripeService';

interface Subscription {
  id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  payment_method_last4: string | null;
  payment_method_brand: string | null;
  stripe_subscription_id: string | null;
}

interface PaymentHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  invoice_url: string | null;
  failure_reason: string | null;
}

export function SubscriptionTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);

  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load subscription
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (subError && subError.code !== 'PGRST116') throw subError;
      setSubscription(subData);

      // Load payment history
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (paymentError) throw paymentError;
      setPaymentHistory(paymentData || []);
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Error al cargar la información de suscripción');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setPortalLoading(true);
      const portalUrl = await openCustomerPortal();
      window.open(portalUrl, '_blank');
    } catch (error) {
      console.error('Error opening portal:', error);
      toast.error(error instanceof Error ? error.message : 'Error al abrir el portal de pagos');
    } finally {
      setPortalLoading(false);
    }
  };

  const getPlanBadge = (plan: string): 'default' | 'secondary' => {
    const variants: Record<string, 'default' | 'secondary'> = {
      free: 'secondary',
      pago: 'default',
      oposiciones: 'default',
    };
    return variants[plan] || 'secondary';
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; variant: 'default' | 'destructive' | 'outline' | 'secondary' }> = {
      active: { label: 'Activa', variant: 'default' },
      canceled: { label: 'Cancelada', variant: 'secondary' },
      past_due: { label: 'Pago pendiente', variant: 'destructive' },
      trialing: { label: 'Periodo de prueba', variant: 'secondary' },
      paused: { label: 'Pausada', variant: 'secondary' },
    };
    const statusConfig = config[status] || { label: status, variant: 'secondary' as const };
    return <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>;
  };

  const getPaymentStatusBadge = (status: string) => {
    const config: Record<string, { label: string; variant: 'default' | 'destructive' | 'outline' | 'secondary' }> = {
      succeeded: { label: 'Exitoso', variant: 'default' },
      failed: { label: 'Fallido', variant: 'destructive' },
      pending: { label: 'Pendiente', variant: 'secondary' },
      refunded: { label: 'Reembolsado', variant: 'secondary' },
    };
    const statusConfig = config[status] || { label: status, variant: 'secondary' as const };
    return <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No se encontró información de suscripción</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan actual */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Plan Actual</h3>
            <div className="flex items-center gap-2">
              <Badge variant={getPlanBadge(subscription.plan)} className="text-lg px-4 py-1">
                {subscription.plan === 'free' && 'Plan Gratuito'}
                {subscription.plan === 'oposiciones_basic' && 'Munilex Academy · Básico'}
                {subscription.plan === 'base_comun' && 'Munilex Academy · Base Común Pro'}
                {subscription.plan === 'oposiciones' && 'Munilex Academy · 1 Oposición Pro'}
                {(subscription.plan === 'multi-oposicion' || subscription.plan === 'multi_oposicion') && 'Munilex Academy · All-Access'}
                {subscription.plan === 'pro' && 'Plan Pro Funcionarios'}
              </Badge>
              {getStatusBadge(subscription.status)}
            </div>
          </div>

          {subscription.cancel_at_period_end && (
            <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Cancelación programada</p>
                <p className="text-sm text-muted-foreground">
                  Tu suscripción se cancelará al final del periodo actual
                </p>
              </div>
            </div>
          )}

          {subscription.current_period_end && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>
                Próxima renovación: {format(new Date(subscription.current_period_end), "d 'de' MMMM, yyyy", { locale: es })}
              </span>
            </div>
          )}

          {subscription.payment_method_last4 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CreditCard className="h-4 w-4" />
              <span>
                {subscription.payment_method_brand?.toUpperCase()} •••• {subscription.payment_method_last4}
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={() => navigate('/pricing')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Cambiar de plan
            </Button>
            {subscription.plan !== 'free' && subscription.stripe_subscription_id && (
              <Button 
                variant="outline" 
                onClick={handleOpenPortal}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Settings className="h-4 w-4 mr-2" />
                )}
                Gestionar suscripción
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Historial de pagos */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Historial de Pagos</h3>
        {paymentHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay pagos registrados
          </p>
        ) : (
          <div className="space-y-2">
            {paymentHistory.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">
                      {payment.amount.toFixed(2)} {payment.currency}
                    </span>
                    {getPaymentStatusBadge(payment.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(payment.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </p>
                  {payment.failure_reason && (
                    <p className="text-sm text-destructive mt-1">{payment.failure_reason}</p>
                  )}
                </div>
                {payment.invoice_url && (
                  <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
